import React, { useEffect, useRef, useState } from 'react';
import {
  useSchedulingContext,
  useSchedulingUpdateContext,
} from '../../../context';
import {
  EmptyContactDetails,
  PatientDetails,
  VerifyPatientByMrnAndDobResult,
} from '../../../../api/models';
import PatientStepPresenter from './PatientStepPresenter';
import ReCAPTCHA from 'react-google-recaptcha';
import { ReCaptchaState, validateReCaptcha } from '../../../reCaptchaValidator';
import {
  SendPatientAccessCodeApiService,
  SendPatientAccessCodeRequest,
  VerifyByMHNAndDateOfBirthApiService,
  VerifyByMHNAndDateOfBirthRequest,
  VerifyPatientAccessCodeApiService,
  VerifyPatientAccessCodeRequest,
} from '../../../../api/services';
import { PhoneUtility } from '@mchs/web-core';

export interface PatientStepWrapperProps {
  onContinue: () => void;
}

const PatientStepWrapper: React.FC<PatientStepWrapperProps> = (
  props: PatientStepWrapperProps
) => {
  const schedulingDetails = useSchedulingContext();
  const schedulingUpdateContext = useSchedulingUpdateContext();

  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [reCaptchaState, setReCaptchaState] = useState<ReCaptchaState>(
    ReCaptchaState.OK
  );

  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  const validateReCaptchaCall = async (): Promise<boolean> => {
    if (!loaded) {
      return false;
    }

    const newReCaptchaState = await validateReCaptcha(reCaptchaRef);
    setReCaptchaState(newReCaptchaState);

    return newReCaptchaState === ReCaptchaState.OK;
  };

  const validateMrnAndDob = async (): Promise<
    VerifyPatientByMrnAndDobResult | undefined
  > => {
    if (
      !loaded ||
      schedulingDetails.patientDetails?.demographics?.mrn === undefined ||
      schedulingDetails.patientDetails?.demographics?.dateOfBirth ===
        undefined ||
      schedulingDetails.patientDetails?.demographics?.dateOfBirth === null
    ) {
      return undefined;
    }

    const request: VerifyByMHNAndDateOfBirthRequest = {
      patientMhn: schedulingDetails.patientDetails.demographics.mrn,
      dateOfBirth:
        schedulingDetails.patientDetails.demographics.dateOfBirth ?? undefined,
    };

    const response = await VerifyByMHNAndDateOfBirthApiService.execute(request);

    return response.succeeded ? response.data : undefined;
  };

  const onReCaptchaChange = () => {
    setReCaptchaState(ReCaptchaState.OK);
  };

  const onChangePatientDetails = (patientDetails: PatientDetails) => {
    schedulingUpdateContext.changeSchedulingDetails({
      ...schedulingDetails,
      patientDetails: { ...patientDetails },
    });
  };

  const sendAccessCode = async (
    mrn: string,
    dateOfBirth: string,
    contactType: string,
    phoneKey: string
  ): Promise<boolean | undefined> => {
    if (
      !loaded ||
      mrn.length === 0 ||
      dateOfBirth === undefined ||
      dateOfBirth === null ||
      contactType.length === 0 ||
      phoneKey.length === 0
    ) {
      return undefined;
    }

    const request: SendPatientAccessCodeRequest = {
      patientMhn: mrn,
      dateOfBirth: dateOfBirth ?? undefined,
      accessCodeType: contactType,
      phoneNumberKeySelected: phoneKey,
    };

    const response = await SendPatientAccessCodeApiService.execute(request);

    return response.succeeded
      ? response.data?.patientAccessCodeSent
      : undefined;
  };

  const verifyAccessCode = async (
    mrn: string,
    dateOfBirth: string,
    contactType: string,
    patientAccessCode: string
  ): Promise<boolean | undefined> => {
    if (
      !loaded ||
      mrn.length === 0 ||
      dateOfBirth === undefined ||
      dateOfBirth === null ||
      contactType.length === 0 ||
      patientAccessCode.length === 0
    ) {
      return undefined;
    }

    const request: VerifyPatientAccessCodeRequest = {
      patientMhn: mrn,
      dateOfBirth: dateOfBirth ?? undefined,
      accessCodeType: contactType,
      accessCode: patientAccessCode,
    };

    const response = await VerifyPatientAccessCodeApiService.execute(request);

    const success =
      response.succeeded &&
      response.data &&
      response.data.patientAccessCodeVerified;

    if (success) {
      const verifiedPatient = response.data?.verifiedPatient;
      schedulingUpdateContext.changeSchedulingDetails({
        ...schedulingDetails,
        patientDetails: {
          ...schedulingDetails.patientDetails,
          demographics: {
            ...schedulingDetails.patientDetails?.demographics,
            firstName: verifiedPatient?.name?.firstName ?? '',
            lastName: verifiedPatient?.name?.lastName ?? '',
            middleName: verifiedPatient?.name?.middleName ?? '',
            nameSuffix: verifiedPatient?.name?.suffix ?? '',
            dateOfBirth: dateOfBirth,
            preferredSex: verifiedPatient?.preferredSex ?? '',
          },
        },
        contactInfoDetails: {
          patientContactDetails: {
            ...EmptyContactDetails,
            phoneNumber: verifiedPatient?.contactInfo?.homePhoneNumber
              ? PhoneUtility.cleanPhone(
                  verifiedPatient.contactInfo.homePhoneNumber
                )
              : '',
            mobileNumber: verifiedPatient?.contactInfo?.mobilePhoneNumber
              ? PhoneUtility.cleanPhone(
                  verifiedPatient.contactInfo.mobilePhoneNumber
                )
              : '',
            emailAddress: verifiedPatient?.contactInfo?.emailAddress ?? '',
          },
        },
      });
    }

    return success;
  };

  return (
    <PatientStepPresenter
      schedulingDetails={schedulingDetails}
      reCaptchaRef={reCaptchaRef}
      reCaptchaState={reCaptchaState}
      onReCaptchaChange={onReCaptchaChange}
      validateReCaptcha={validateReCaptchaCall}
      onChangePatientDetails={onChangePatientDetails}
      validateMrnAndDob={validateMrnAndDob}
      sendAccessCode={sendAccessCode}
      verifyAccessCode={verifyAccessCode}
      onContinue={props.onContinue}
    />
  );
};

export default PatientStepWrapper;
